import { supabase } from '../client';
import { B2B_CLIENT_COMPLIANCE_BUCKET } from '../storageConfig';
import type { Client, ClientComplianceDocument, ClientComplianceDocStatus } from '@core/types';
import { REQUIRED_CLIENT_COMPLIANCE_DOC_TYPES } from '@core/constants/clientOnboarding';
import { clientService } from './clientService';

function requiredSlugs(): string[] {
  return REQUIRED_CLIENT_COMPLIANCE_DOC_TYPES.map((t) => t.slug);
}

function allRequiredApproved(docs: ClientComplianceDocument[]): boolean {
  const approved = new Set(docs.filter((d) => d.status === 'approved').map((d) => d.doc_type));
  return requiredSlugs().every((slug) => approved.has(slug));
}

export const clientComplianceService = {
  async listByClient(clientId: string): Promise<ClientComplianceDocument[]> {
    const { data, error } = await supabase
      .from('ggj_b2b_client_compliance_documents')
      .select('*')
      .eq('client_id', clientId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data ?? []) as ClientComplianceDocument[];
  },

  async uploadDocument(
    orgId: string,
    clientId: string,
    docType: string,
    file: File,
    uploadedBy: string | null
  ): Promise<ClientComplianceDocument> {
    const safeName = file.name.replace(/[^\w.\-()+ ]/g, '_').slice(0, 180);
    const uid = crypto.randomUUID();
    const path = `client-compliance/${orgId}/${clientId}/${docType}/${uid}-${safeName}`;
    const { error: upErr } = await supabase.storage
      .from(B2B_CLIENT_COMPLIANCE_BUCKET)
      .upload(path, file, { upsert: false });
    if (upErr) throw upErr;

    const { data, error } = await supabase
      .from('ggj_b2b_client_compliance_documents')
      .insert({
        org_id: orgId,
        client_id: clientId,
        doc_type: docType,
        storage_path: path,
        file_name: file.name,
        status: 'pending_review',
        uploaded_by: uploadedBy,
      })
      .select()
      .single();
    if (error) throw error;
    return data as ClientComplianceDocument;
  },

  getPublicUrl(storagePath: string): string {
    const { data } = supabase.storage.from(B2B_CLIENT_COMPLIANCE_BUCKET).getPublicUrl(storagePath);
    return data.publicUrl;
  },

  async updateDocumentStatus(
    id: string,
    status: ClientComplianceDocStatus,
    reviewedBy: string | null,
    reviewNote: string | null
  ): Promise<ClientComplianceDocument> {
    const { data, error } = await supabase
      .from('ggj_b2b_client_compliance_documents')
      .update({
        status,
        reviewed_at: new Date().toISOString(),
        reviewed_by: reviewedBy,
        review_note: reviewNote?.trim() || null,
      })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data as ClientComplianceDocument;
  },

  async reviewAndSyncClient(
    docId: string,
    clientId: string,
    status: ClientComplianceDocStatus,
    reviewedBy: string | null,
    reviewNote: string | null
  ): Promise<{ doc: ClientComplianceDocument; client: Client }> {
    const doc = await this.updateDocumentStatus(docId, status, reviewedBy, reviewNote);
    const client = await this.syncClientReadiness(clientId, reviewedBy);
    return { doc, client };
  },

  async syncClientReadiness(clientId: string, verifierNameWhenReady: string | null): Promise<Client> {
    const docs = await this.listByClient(clientId);
    const ready = allRequiredApproved(docs);
    const now = new Date().toISOString();
    return clientService.update(clientId, {
      client_onboarding_status: ready ? 'ready_to_recruit' : 'onboarding',
      onboarding_verified_at: ready ? now : null,
      onboarding_verified_by: ready ? verifierNameWhenReady : null,
    });
  },

  async markReadyOverride(clientId: string, verifiedBy: string | null, note: string | null): Promise<Client> {
    return clientService.update(clientId, {
      client_onboarding_status: 'ready_to_recruit',
      onboarding_verified_at: new Date().toISOString(),
      onboarding_verified_by: verifiedBy,
      compliance_pending_request_note: note?.trim() || null,
    });
  },
};
